import { Component, ComponentChild, h } from 'preact'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import { WidgetSetting } from './Types'
import { common } from '../common'
import DragHandle from '../control/DragHandle'
import InputGroup from '../ui-components/InputGroup'
import Icon from '../assets'
import ButtonControl from '../button-control/index'
import Checkbox from './../../preact-components/ui-components/Checkbox'
import ImageUploader from '../category/control/ImageUploader'

const SortableList = SortableContainer(({ children }: { children: ComponentChild }) => <ul className='sortable-navigation-link-list'>{children}</ul>)
const SortableItem = SortableElement(({ children }: { children: ComponentChild }) => <li className='sortable-navigation-link'>{children}</li>)

interface Props {
  items: Array<WidgetSetting>,
  logo: string,
  onChange: (config: {items: Array<WidgetSetting>, logo: string}) => void
}

interface State {
  items: Array<WidgetSetting>,
  logo: string
}

class HeaderNavigationConfiguration extends Component<Props, State> {
  itemsNameMapping: {
    [key: string]: string
  } = {
    community: 'Community',
    knowledgeBase: 'Knowledge Base',
    ideation: 'Ideation',
    productUpdates: 'Product Updates',
    event: 'Events',
    group: 'Groups',
    custom: 'Custom Link'
  }

  // Add all possible new modules here to appear on edit panel
  itemsMapping: {
    [key: string]: WidgetSetting
  } = {
    community: {
      key: 'community',
      name: 'Community',
      visibility: false
    },
    knowledgeBase: {
      key: 'knowledgeBase',
      name: 'Knowledge Base',
      visibility: false
    },
    ideation: {
      key: 'ideation',
      name: 'Ideation',
      visibility: false,
      url: '/ideas'
    },
    productUpdates: {
      key: 'productUpdates',
      name: 'Product Updates',
      visibility: false,
      url: '/product-updates'
    },
    event: {
      key: 'event',
      name: 'Events',
      visibility: false,
      url: '/events'
    },
    group: {
      key: 'group',
      name: 'Groups',
      visibility: false,
      url: '/groups'
    },
    custom: {
      key: 'custom',
      name: 'Custom Link',
      visibility: false
    }
  }
  uploader: HTMLInputElement | undefined
  constructor (props: Props) {
    super(props)
    let newItemList: Array<WidgetSetting> = props.items

    // This section will run if there is no setting yet, it will display all possibilities with visibility true
    if (newItemList.length === 0) {
      newItemList = [
        {
          key: 'community',
          visibility: true,
          name: 'Community'
        }
      ]
      if (common.isModuleActive('knowledgeBase')) {
        newItemList.push({
          key: 'knowledgeBase',
          visibility: true,
          name: 'Knowledge Base'
        })
      }

      if (common.isModuleActive('ideationV2')) {
        newItemList.push({
          key: 'ideation',
          visibility: true,
          name: 'Ideas',
          url: '/ideas'
        })
      }

      if (common.isModuleActive('productUpdates')) {
        newItemList.push({
          key: 'productUpdates',
          visibility: true,
          name: 'Product Updates',
          url: '/product-updates'
        })
      }

      if (common.isModuleActive('event')) {
        newItemList.push({
          key: 'event',
          visibility: true,
          name: 'Events',
          url: '/events'
        })
      }

      if (common.isModuleActive('group')) {
        newItemList.push({
          key: 'group',
          visibility: true,
          name: 'Groups',
          url: '/groups'
        })
      }
    } else { // This section will check if there is a new module which is not part of customers setting and will show new modules at the end of setting with visibility false
      let itemsClone = Object.assign({}, this.itemsMapping)
      newItemList.forEach(item => {
        delete itemsClone[item.key]
      })

      Object.keys(itemsClone).forEach(leftItemKey => (
        newItemList.push(this.itemsMapping[leftItemKey])
      ))
    }

    this.state = {
      items: newItemList,
      logo: props.logo
    }

    this.notifyChanges = this.notifyChanges.bind(this)
    this.onItemUpdated = this.onItemUpdated.bind(this)
    this.onItemNameChanged = this.onItemNameChanged.bind(this)
    this.onItemVisibilityChanged = this.onItemVisibilityChanged.bind(this)
    this.onAddCustomLink = this.onAddCustomLink.bind(this)
    this.onItemUrlChanged = this.onItemUrlChanged.bind(this)
    this.onItemExternalChanged = this.onItemExternalChanged.bind(this)
    this.onItemRemoved = this.onItemRemoved.bind(this)
    this.onLogoChanged = this.onLogoChanged.bind(this)
  }

  notifyChanges () {
    this.props.onChange({ items: this.state.items, logo: this.state.logo })
  }

  onAddCustomLink () {
    let newItems = this.state.items.concat([])
    newItems.push({
      key: 'custom',
      visibility: true,
      name: 'Custom link',
      url: '',
      external: false
    })
    this.onItemUpdated(newItems)
  }

  onItemNameChanged (idx: number, newName: string) {
    let newItems = this.state.items.concat([])
    newItems[idx].name = newName
    this.onItemUpdated(newItems)
  }

  onItemRemoved (idx: number) {
    let newItems = this.state.items.concat([])
    newItems.splice(idx, 1)
    this.onItemUpdated(newItems)
  }

  onItemUrlChanged (idx: number, newUrl: string) {
    let newItems = this.state.items.concat([])
    newItems[idx].url = newUrl
    this.onItemUpdated(newItems)
  }

  onItemVisibilityChanged (idx: number) {
    let newItems = this.state.items.concat([])
    newItems[idx].visibility = !newItems[idx].visibility
    this.onItemUpdated(newItems)
  }

  onItemExternalChanged (idx: number) {
    let newItems = this.state.items.concat([])
    newItems[idx].external = !newItems[idx].external
    this.onItemUpdated(newItems)
  }

  onItemUpdated (newItems: Array<WidgetSetting>) {
    this.setState({
      items: newItems
    }, this.notifyChanges)
  }

  onLogoChanged (newLogo: string) {
    this.setState({
      logo: newLogo
    }, this.notifyChanges)
  }

  render (_:Props, { items, logo }: State) {
    return (
      <div>
        <div className='widget-configuration_info-box'>
          <strong>✨ What’s a shared widget? </strong>
          <p>As a shared widget, the configurations of this widget would be used on all pages in community.</p>
        </div>

        <div className='header-navigation_logo'>
          <p><strong>Logo</strong></p>
          <p>We recommend  height of 80px and width smaller than 400px</p>
          <div className='form-group'>
            {
              logo === '' ? (
                <div>
                  <ButtonControl
                    newStyle
                    variant='secondary'
                    title='Upload image'
                    onClick={() => this.uploader?.click()}
                  />
                </div>
              ) : (
                (
                  <div className='header-navigation_logo-configurator-container'>
                    <div className='header-navigation_logo-wrapper'>
                      <img src={logo} />
                    </div>
                    <div className='header-navigation_logo_buttons'>
                      <ButtonControl
                        newStyle
                        variant='secondary'
                        title='Edit'
                        onClick={() => this.uploader?.click()}
                      />
                      <ButtonControl
                        newStyle
                        variant='danger'
                        title='Remove'
                        onClick={() => this.onLogoChanged('')}
                      />
                    </div>
                  </div>
                )
              )
            }
          </div>
          <ImageUploader
            onUploaded={({ source_thumb }: { source_thumb: string }) => this.onLogoChanged(source_thumb)}
            uploaderRef={(ref: HTMLInputElement) => { this.uploader = ref }}
          />
        </div>

        <div className='header-navigation_links-list'>
          <p><strong>Navigation links</strong></p>
          <SortableList
            useDragHandle
            onSortEnd={({ oldIndex, newIndex } : {oldIndex: number, newIndex: number}) => this.onItemUpdated(arrayMove(items, oldIndex, newIndex))}
          >
            {
              items.map((item, idx) => (
                <SortableItem index={idx} key={idx}>
                  <div className='header-navigation_item-configurator'>
                    <div className='header-navigation_item-configurator-title'>
                      <span className='header-navigation_item-configurator-title-text'><DragHandle />{ this.itemsNameMapping[item.key] }</span>
                      <div>
                        <span className='header-navigation_item-configurator-visibility' onClick={() => this.onItemVisibilityChanged(idx)}>
                          <Icon
                            color='#6B707E'
                            icon={item.visibility ? 'eye' : 'eye-cross'}
                            width={18} height={18}
                          />
                        </span>
                        {
                          item.key === 'custom' ? (
                            <span onClick={() => this.onItemRemoved(idx)}>
                              <Icon
                                icon='trash'
                                color='#6B707E'
                                customClass='header-navigation_item-configurator-trash'
                                width={18} height={18}
                              />
                            </span>
                          ) : null
                        }
                      </div>
                    </div>
                    <InputGroup
                      name='name'
                      placeholder='Custom link name'
                      type='text'
                      onChange={(e: {target: HTMLInputElement}) => { this.onItemNameChanged(idx, e.target.value) }}
                      value={item.name} />
                    {
                      item.key === 'custom' ? (
                        <InputGroup
                          name='url'
                          placeholder='URL'
                          type='text'
                          onChange={(e: {target: HTMLInputElement}) => { this.onItemUrlChanged(idx, e.target.value) }}
                          value={item.url} />
                      ) : null
                    }
                    {
                      item.key === 'custom' ? (
                        <Checkbox
                          label='Open in new tab'
                          onUpdate={() => this.onItemExternalChanged(idx)}
                          checked={!!item.external}
                        />
                      ) : null
                    }
                  </div>
                </SortableItem>
              ))
            }
          </SortableList>
          <ButtonControl
            newStyle
            variant='secondary' title='Add custom link' onClick={this.onAddCustomLink} />
        </div>
      </div>
    )
  }
}

export default HeaderNavigationConfiguration
